<template>
    <section class="flex flex-col gap-5">
        <header class="flex flex-col gap-2">
            <div class="flex items-center gap-3 flex-wrap">
                <ReflectionIcon kind="self" size="title" />
                <h2 class="title-subsection text-valence-stone-900">Self Reflection</h2>
                <TabInfoToggle v-if="hasAnyItem" :expanded="infoOpen" topic="self reflection" @toggle="infoOpen = !infoOpen" />
                <!-- LAB-988 dark CTA override — see .base-btn-dark in tailwind.css. -->
                <a
                    v-if="hasAnyItem"
                    href="#"
                    class="base-btn-dark base-btn-sm ml-auto inline-flex items-center gap-1.5 shrink-0 whitespace-nowrap"
                    @click.prevent="emit('start-blank')">
                    <i class="bi bi-plus-lg" aria-hidden="true"></i>
                    Start reflection
                </a>
            </div>
            <p class="body-small text-valence-stone-500">Reflect on how your half has gone — your reflection grounds your review and the coaching you get from Nadia.</p>
        </header>

        <hr class="border-t border-valence-cream-300" />

        <HowSelfReflectionWorks v-if="!hasAnyItem" @start="emit('start-blank')" />

        <BaseDialog v-model:open="infoOpen" title="How self reflections work" show-close float-close dialog-content-class="md:!max-w-2xl">
            <HowSelfReflectionWorks @start="onStartFromInfo" />
        </BaseDialog>

        <div v-if="pendingTasks.length" class="flex flex-col gap-2">
            <div class="scaffold-details-small text-valence-stone-500">Ready to start</div>
            <SelfTaskRow v-for="task in pendingTasks" :key="task.id" :task="task" :show-icon="false" @start="onStart" />
        </div>

        <div v-if="pastReflections.length" class="flex flex-col gap-2 mt-5">
            <div class="scaffold-details-small text-valence-stone-500">Previous Reflections</div>
            <PastCycleCard v-for="cycle in pastReflections" :key="cycle.id" :cycle="cycle" kind="self" @view="emit('view-cycle', $event)" />
        </div>

        <AssessmentDemoTools :cycle-actions="cycleDemoActions" bootstrap-kind="self" @deleted="emit('deleted', $event)" />
    </section>
</template>

<script setup lang="ts">
import type { PastCycleSummary, PendingPersonalTask } from "~vue/api/generated/reflections";
import BaseDialog from "~vue/components/BaseDialog.vue";
import AssessmentDemoTools from "~vue/pages/reflections/AssessmentDemoTools.vue";
import HowSelfReflectionWorks from "~vue/pages/reflections/HowSelfReflectionWorks.vue";
import PastCycleCard from "~vue/pages/reflections/PastCycleCard.vue";
import ReflectionIcon from "~vue/pages/reflections/ReflectionIcon.vue";
import SelfTaskRow from "~vue/pages/reflections/SelfTaskRow.vue";
import TabInfoToggle from "~vue/pages/reflections/TabInfoToggle.vue";
import { computed, ref } from "vue";

interface Props {
    pendingTasks: PendingPersonalTask[];
    pastReflections: PastCycleSummary[];
}

const props = defineProps<Props>();
const emit = defineEmits<{
    (event: "start", payload: { cycleId: string; title: string; closesAt: string | null }): void;
    (event: "view-cycle", cycleId: string): void;
    (event: "start-from-campaign", payload: { campaignId: string; title: string; isSelfAssessmentOnly: boolean }): void;
    (event: "start-blank"): void;
    (event: "deleted", cycleId: string): void;
}>();

function onStart(task: PendingPersonalTask) {
    if (task.kind === "self_reflection_cycle") {
        emit("start", { cycleId: task.id, title: task.title, closesAt: task.closes_at ?? null });
        return;
    }
    if (task.kind === "self_reflection_campaign") {
        emit("start-from-campaign", { campaignId: task.id, title: task.title, isSelfAssessmentOnly: true });
    }
}

const hasAnyItem = computed(() => props.pendingTasks.length > 0 || props.pastReflections.length > 0);
// Campaign-kind tasks carry a campaign id rather than a cycle id, so they have
// no cycle to fill or delete yet.
const cycleDemoActions = computed(() => [
    ...props.pendingTasks.filter((task) => task.kind === "self_reflection_cycle").map((task) => ({ id: task.id, label: task.title })),
    ...props.pastReflections.map((cycle) => ({ id: cycle.id, label: cycle.template.name, canFill: false })),
]);
const infoOpen = ref(false);

function onStartFromInfo() {
    infoOpen.value = false;
    emit("start-blank");
}
</script>
